/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { Currencies } from '../../../models/Currencies';
import { CreateOrUpdateCampaignReqBody } from '../../../models/campaign/CampaignCreateOrUpdateRequest';
import {
  CampaignExpertPayoutResult,
  CampaignPayoutStatus,
} from '../../../models/campaign/CampaignPayoutResult';
import { PartnerTable } from '../../../models/partnerTable';
import { CirclesService } from '../../../services/circles.service';
import { CampaignService } from '../../../services/campaign.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { PartnerService } from '../../../services/partner.service';

@Component({
  selector: 'app-crud-campaign',
  templateUrl: './crud-campaign.component.html',
  styleUrl: './crud-campaign.component.scss',
})
export class CrudCampaignComponent extends AnyoComponent {
  campaignId = '';
  isEdit = false;
  viewMode = true;
  loading = false;

  validateForm: FormGroup;
  dateFormat = 'dd-MM-yyyy HH:mm';

  campaignCategories: { label: string; value: string }[] = [];
  partners: PartnerTable[] = [];
  selectedPartnerId = '';
  isVisible = true;

  circleExpertsList: { expertId: string; name: string }[] = [];
  presenterRows: {
    presenterId: string | null;
    isPaid: boolean;
    amount: number | null;
    // Payout was already saved for this presenter on the server — the amount can't be edited or
    // removed once entered, so this row's payout controls stay disabled even in edit mode.
    alreadyPaid: boolean;
  }[] = [
    { presenterId: null, isPaid: false, amount: null, alreadyPaid: false },
  ];

  offlineCampaign = false;
  venue: string | undefined;
  testCampaign = false;

  image: File | undefined;
  imageLink = '';
  imagePreviewUrl: string | null = null;

  activityImages: File[] = [];
  activityImagesLinks: string[] = [];
  activityImagesPreviewUrls: string[] = [];

  galleryVisible = false;
  galleryImage: string | null = null;

  payoutResultsModalVisible = false;
  payoutResults: CampaignExpertPayoutResult[] = [];

  constructor(
    auth: AuthService,
    private fb: NonNullableFormBuilder,
    private activatedRoute: ActivatedRoute,
    private masterData: MasterdataService,
    private campaignService: CampaignService,
    private toasterService: ToastService,
    private circleService: CirclesService,
    private router: Router,
    private partnerService: PartnerService,
    private fileService: FileUtilsService,
  ) {
    super(auth);
    this.validateForm = fb.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      durationMinutes: [0, [Validators.required, Validators.min(10)]],
      selectedCategory: ['', [Validators.required]],
      campaignDate: [new Date(), [Validators.required]],
      numberOfAttendees: [null, [Validators.min(0)]],
    });
    this.validateForm.disable();
  }

  async ready(): Promise<void> {
    if (this.user) {
      this.pageLoading = true;
      const masterData = await lastValueFrom(this.masterData.masterDataList());
      this.campaignCategories = masterData.eventCategories.map((value) => {
        return {
          value: value.m_id,
          label: value.data,
        };
      });
      const circleMasterDataResponse = await lastValueFrom(
        this.circleService.getCircleMasterData(),
      );
      this.circleExpertsList = circleMasterDataResponse.circleExperts.map(
        (value) => {
          return {
            expertId: value.name,
            name: value._id,
          };
        },
      );
      this.partners = await this.partnerService.getAllActivePartners();

      const id = this.activatedRoute.snapshot.params['id'];
      this.campaignId = id;

      if (id) {
        this.isEdit = true;
        this.viewMode = true;
        await this.getAndSetCampaign(id);
      } else {
        this.isEdit = false;
        this.viewMode = false;
        this.validateForm.enable();
      }
      this.pageLoading = false;
    }
  }

  editForm() {
    this.viewMode = false;
    this.validateForm.enable();
  }

  addPresenterRow() {
    this.presenterRows.push({
      presenterId: null,
      isPaid: false,
      amount: null,
      alreadyPaid: false,
    });
  }

  filteredExperts(index: number) {
    const selected = this.presenterRows
      .map((r) => r.presenterId)
      .filter((x) => x !== null);

    return this.circleExpertsList.filter(
      (expert) =>
        !selected.includes(expert.name) ||
        this.presenterRows[index].presenterId === expert.name,
    );
  }

  removeRow(index: number) {
    this.presenterRows.splice(index, 1);
  }

  onPresenterChange(index: number) {
    const row = this.presenterRows[index];
    row.isPaid = false;
    row.amount = null;
  }

  uploadImage($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.image = file;
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
      }
      this.imagePreviewUrl = URL.createObjectURL(file);
    }
  }

  uploadActivityImages($event: any) {
    const files = $event.target.files as FileList;
    if (files && files.length) {
      const filesArray: File[] = Array.from(files);
      this.activityImages.push(...filesArray);
      for (const f of filesArray) {
        this.activityImagesPreviewUrls.push(URL.createObjectURL(f));
      }
    }
  }

  deleteActivityImagePreview(index: number) {
    URL.revokeObjectURL(this.activityImagesPreviewUrls[index]);
    this.activityImagesPreviewUrls.splice(index, 1);
    this.activityImages.splice(index, 1);
  }

  deleteActivityImage(index: number) {
    this.activityImagesLinks.splice(index, 1);
  }

  openGallery(image: string) {
    this.galleryImage = image;
    this.galleryVisible = true;
  }

  closeGallery() {
    this.galleryVisible = false;
    this.galleryImage = null;
  }

  acknowledgePayoutResults() {
    this.payoutResultsModalVisible = false;
    this.router.navigate(['/campaigns']);
  }

  payoutStatusColor(status: CampaignPayoutStatus): string {
    switch (status) {
      case CampaignPayoutStatus.DONE:
        return 'green';
      case CampaignPayoutStatus.SKIPPED:
        return 'orange';
      case CampaignPayoutStatus.FAILED:
        return 'red';
      default:
        return 'default';
    }
  }

  async getAndSetCampaign(id: string): Promise<void> {
    this.campaignService.getCampaign(id).subscribe({
      next: (value) => {
        this.selectedPartnerId = value.partnerId || '';
        this.isVisible = value.isVisible;
        this.imageLink = value.image || '';
        this.offlineCampaign =
          value.tags?.includes('OFFLINE_CAMPAIGN') || false;
        this.venue = value.venue;
        this.activityImagesLinks = value.activityImages || [];

        this.validateForm.patchValue({
          title: value.title,
          description: value.description,
          durationMinutes: value.durationMinutes,
          selectedCategory: value.categoryId,
          campaignDate: value.startDate,
          numberOfAttendees: value.numberOfAttendees ?? null,
        });

        this.presenterRows = [];
        const presenterIds = value.presenterDetailIds || [];
        const payoutMap = new Map<string, number>();
        if (value.payoutDetails && value.payoutDetails.length) {
          value.payoutDetails.forEach((p) => {
            payoutMap.set(p.presenterId, p.payout.amount);
          });
        }
        presenterIds.forEach((id) => {
          const paidAmount = payoutMap.get(id);
          this.presenterRows.push({
            presenterId: id,
            isPaid: paidAmount !== undefined,
            amount: paidAmount ?? null,
            alreadyPaid: paidAmount !== undefined,
          });
        });
        if (this.presenterRows.length === 0) {
          this.presenterRows.push({
            presenterId: null,
            isPaid: false,
            amount: null,
            alreadyPaid: false,
          });
        }

        this.validateForm.disable();
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(
          errorBody.description || 'Failed to load campaign',
        );
      },
    });
  }

  async addOrUpdateCampaign(): Promise<void> {
    if (!this.validateForm.valid) {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          this.toasterService.showError(
            'Check Any Field has got Error Message',
          );
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const formValue = this.validateForm.value;

    if (!this.selectedPartnerId) {
      this.toasterService.showAnyoErrorToast('Please select a partner');
      return;
    }

    const validPresenters = this.presenterRows.filter(
      (x) => x.presenterId !== null,
    );
    if (!validPresenters.length) {
      this.toasterService.showAnyoErrorToast(
        'Please select at least one Presenter',
      );
      return;
    }

    const invalidPayouts = this.presenterRows.filter(
      (x) => x.presenterId && x.isPaid && (!x.amount || x.amount <= 0),
    );
    if (invalidPayouts.length > 0) {
      this.toasterService.showAnyoErrorToast(
        'Please enter payout amount for presenters marked as paid',
      );
      return;
    }

    if (this.offlineCampaign && !this.venue) {
      this.toasterService.showAnyoErrorToast(
        'Please enter venue for offline campaign',
      );
      return;
    }

    try {
      const validRows = this.presenterRows.filter((x) => x.presenterId);
      const presenterDetailIds = validRows.map((x) => x.presenterId!);
      const payoutDetails = validRows
        .filter((x) => x.isPaid)
        .map((x) => ({
          presenterId: x.presenterId!,
          payout: {
            currency: Currencies.INR,
            currencySymbol: '₹',
            amount: x.amount!,
          },
        }));

      const req: CreateOrUpdateCampaignReqBody = {
        title: formValue['title'] as string,
        description: formValue['description'] as string,
        durationMinutes: formValue['durationMinutes'] as number,
        categoryId: formValue['selectedCategory'] as string,
        campaignDate: moment(formValue['campaignDate']).valueOf(),
        isVisible: this.isVisible,
        partnerId: this.selectedPartnerId,
        presenterDetailIds: presenterDetailIds,
        offlineCampaign: this.offlineCampaign,
        venue: this.venue,
        numberOfAttendees:
          (formValue['numberOfAttendees'] as number | null) ?? undefined,
        image: this.imageLink || undefined,
        activityImages: this.activityImagesLinks,
      };

      if (payoutDetails.length > 0) {
        req.payoutDetails = payoutDetails;
      }

      if (!this.isEdit) {
        req.testCampaign = this.testCampaign;
      }

      this.loading = true;

      if (this.image) {
        const uuid = uuidv4().replace(/-/g, '');
        const extension = this.image.name.split('.').pop();
        const filePath = `campaigns/${uuid}/image_.${extension}`;
        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.image.type,
          ),
        );
        this.toasterService.showSuccess('Uploading Image');
        await lastValueFrom(
          this.fileService.uploadFile(this.image, uploadUrl.url),
        );
        this.toasterService.showSuccess('Uploading Image completed');
        req.image = `https://cdn.anyo.app/${filePath}`;
        // for development purpose, we are not uploading the image to S3, instead we are using the local image link
        // req.image = 'https://cdn.anyo.app/events/38d787ffd1a648f5bc951724afb29dd8/images_.jpg';
      }

      if (this.activityImages.length) {
        const uploadedUrls: string[] = [];
        for (let i = 0; i < this.activityImages.length; i++) {
          const file = this.activityImages[i];
          const uuid = uuidv4().replace(/-/g, '');
          const extension = file.name.split('.').pop();
          const filePath = `campaigns/${uuid}/activity_.${extension}`;
          const uploadUrl = await lastValueFrom(
            this.fileService.generateUploadUrl(
              AnyoS3Buckets.CDN_BUCKET,
              filePath,
              file.type,
            ),
          );
          this.toasterService.showSuccess(`Uploading activity image ${i + 1}`);
          await lastValueFrom(this.fileService.uploadFile(file, uploadUrl.url));
          uploadedUrls.push(`https://cdn.anyo.app/${filePath}`);
        }
        req.activityImages = [...this.activityImagesLinks, ...uploadedUrls];
      }

      if (this.isEdit) {
        req.campaignId = this.campaignId;
      }

      this.campaignService.addOrUpdateCampaign(req).subscribe({
        next: (response) => {
          this.loading = false;
          this.toasterService.showSuccess(
            `Campaign ${this.isEdit ? 'Updated' : 'Created'} Successfully`,
          );
          if (Array.isArray(response) && response.length) {
            this.payoutResults = response;
            this.payoutResultsModalVisible = true;
          } else {
            this.router.navigate(['/campaigns']);
          }
        },
        error: (error) => {
          this.loading = false;
          const errorBody = error.error as IAnyoError;
          this.toasterService.showAnyoErrorToast(errorBody.description);
        },
      });
    } catch (error) {
      this.loading = false;
      this.toasterService.showError(
        'An error occurred while saving the campaign',
      );
    }
  }
}
